import React, { useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { Formik } from 'formik';

import {
  getTourFeedbackTags,
} from 'common/tourHelpers';

import {
  TOUR_SHOWING_STATUS_COMPLETED,
  TOUR_TYPE_SELF_SHOWING,
} from 'common/tourConstants';

import {
  createInitialValues,
  createFormValidationFn,
} from 'client/helpers/formikHelpers';

import {
  MEDIA_QUERY_SMALL_SCREEN,
} from 'client/constants/mediaQueries';

import useDispatch from 'client/hooks/useDispatch';

import TourActions from 'client/actions/Tour';
import SrAskWantsToApplyModal from 'client/components/SrAskWantstoApplyModal/SrAskWantsToApplyModal';
import SrTourFeedbackStepStars from './SrTourFeedbackStepStars';
import SrTourFeedbackStepFeedback from './SrTourFeedbackStepFeedback';

import './SrTourFeedback.less';

const getNextShowingToReview = ({ tours, tourId }) => {
  const tour = tours.find((tour) => tour.id === tourId);
  if (!tour) {
    return {};
  }

  const listing = tour.listings
    .filter((listing) => {
      return (
        listing.tourListingStatus === TOUR_SHOWING_STATUS_COMPLETED
        || tour.type === TOUR_TYPE_SELF_SHOWING
      );
    })
    .find((listing) => {
      return (
        !listing.tourListingReviewId
        && !listing.tourListingReviewSkipped
      );
    });

  return {
    tour,
    listing,
  };
};

function getFormFields({ values = {}, formikBag } = {}) {
  return [
    {
      id: 'rating',
      type: 'star_rating',
      validations: ['positiveNumber'],
      properties: {
        onChange: () => {
          formikBag.setFieldValue('stepId', 1);
        },
      },
    },
    {
      id: 'tags',
      type: 'tags',
      properties: {
        choices: getTourFeedbackTags(values.rating).map((tag) => ({
          value: tag,
          label: tag,
        })),
      },
    },
    {
      id: 'feedback',
      type: 'long_text',
      properties: {
        placeholder: 'Share your feedback',
      },
    },
    {
      id: 'stepId',
      type: 'hidden',
    },
  ];
}

const SrTourFeedback = ({ tours, tourId, isMobileApp }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSmallScreen = useMediaQuery({
    query: MEDIA_QUERY_SMALL_SCREEN,
  });
  const dispatch = useDispatch();

  const { tour, listing } = getNextShowingToReview({ tours, tourId });
  if (!listing) {
    return null;
  }

  const onClose = (formValues) => {
    if (!formValues.rating) {
      dispatch(TourActions.setShouldRequestRenterTourFeedback, false);
      return;
    }

    const tourFeedbackTags = getTourFeedbackTags(formValues.rating);

    dispatch(TourActions.rateShowing, {
      tourId,
      rating: formValues.rating,
      tags: formValues.tags.filter((tag) => tourFeedbackTags.includes(tag)),
      feedback: formValues.feedback,
      listing: listing,
      skipReview: true,
    });
  };

  const onSubmit = (formValues) => {
    const tourFeedbackTags = getTourFeedbackTags(formValues.rating);
    setIsSubmitting(true);

    dispatch(TourActions.rateShowing, {
      tourId,
      rating: formValues.rating,
      tags: formValues.tags.filter((tag) => tourFeedbackTags.includes(tag)),
      feedback: formValues.feedback,
      listing: listing,
    });
  };

  return (
    <Formik
      validate={(formValues) => {
        const formFields = getFormFields({ formValues });
        return createFormValidationFn(formFields)(formValues);
      }}
      initialValues={createInitialValues({
        formFields: getFormFields(),
        defaultValues: {
          rating: 0,
          stepId: 0,
        },
      })}
      onSubmit={onSubmit}
      render={(formikBag) => {
        const formFields = getFormFields({
          values: formikBag.values,
          formikBag,
        });

        return (
          <>
            {formikBag.values.stepId === 0 && (
              <SrTourFeedbackStepStars
                tour={tour}
                listing={listing}
                formikBag={formikBag}
                formFields={formFields}
                isSmallScreen={isSmallScreen}
                onClose={onClose}
              />
            )}
            {formikBag.values.stepId === 1 && (
              <SrTourFeedbackStepFeedback
                listing={listing}
                formikBag={formikBag}
                formFields={formFields}
                isSmallScreen={isSmallScreen}
                isSubmitting={isSubmitting}
                isMobileApp={isMobileApp}
                onClose={onClose}
              />
            )}
            {formikBag.values.stepId === 2 && (
              <SrAskWantsToApplyModal
                listing={listing}
                formikBag={formikBag}
                formFields={formFields}
                isSmallScreen={isSmallScreen}
                isSubmitting={isSubmitting}
              />
            )}
          </>
        );
      }}
    />
  );
};

SrTourFeedback.displayName = 'SrTourFeedback';

export default SrTourFeedback;
